import { getUserSessionCookie } from '../utils/SessionCookie';
import { apiFetch } from './ApiFetch';
import { getErrorMessageFromResponse } from './httpErrorMessage';
import { BASE_URL } from './constants';
import type { CampaignSpreadsheetViewType } from '../context/CampaignContext';

/** Backend-created domain column name; type url, not editable by user. */
export const DOMAIN_COLUMN_NAME = '$Domain Name';
export const LEAD_COLUMN_NAME = '$Lead';
export const LEAD_COMPANY_COLUMN_NAME = '$Company';
export const PRIMARY_COLUMN_NAMES = [
  DOMAIN_COLUMN_NAME,
  LEAD_COLUMN_NAME,
  LEAD_COMPANY_COLUMN_NAME
];

export const QUALIFIED_COLUMN_NAME = '$Qualified';
export const REASON_COLUMN_NAME = '$Reason';
export const SCORE_COLUMN_NAME = '$Score';
export const TIER_COLUMN_NAME = '$Tier';

/** Built-in prioritization result columns (tiering / qualifying / scoring). */
export const PRIORITIZATION_COLUMN_NAMES = [
  QUALIFIED_COLUMN_NAME,
  REASON_COLUMN_NAME,
  SCORE_COLUMN_NAME,
  TIER_COLUMN_NAME
];

/** True if the column is a primary key column (domain / lead / company); these cannot be reordered or deleted like other columns. */
export function isPrimaryColumnName(columnName: string): boolean {
  return (
    typeof columnName === 'string' && PRIMARY_COLUMN_NAMES.includes(columnName)
  );
}

/** True if the column is a prioritization column ($Qualified, $Reason, $Score, $Tier). */
export function isPrioritizationColumnName(columnName: string): boolean {
  return (
    typeof columnName === 'string' &&
    PRIORITIZATION_COLUMN_NAMES.includes(columnName)
  );
}

/** Return column name for display: strip leading $ if present (matches column header behavior). */
export function displayColumnName(name: string): string {
  if (name == null) return '';
  return name.startsWith('$') ? name.slice(1) : name;
}

/** True if the column is locked (name starts with $). Locked columns cannot have name, type, description, AI enrichment, or other properties changed. */
export function isLockedColumn(columnName: string): boolean {
  return typeof columnName === 'string' && columnName.startsWith('$');
}

/** Appends view_type query param to URL when provided. */
function withViewTypeQueryParam(
  url: string,
  viewType?: CampaignSpreadsheetViewType
): string {
  if (!viewType) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}view_type=${encodeURIComponent(viewType)}`;
}

/** Validates and encodes a column name for safe use in URL paths. */
function validateAndEncodeColumnName(columnName: string): string {
  if (typeof columnName !== 'string' || columnName.trim().length === 0) {
    throw new Error('Invalid column name: must be a non-empty string');
  }
  const trimmed = columnName.trim();
  if (
    trimmed.includes('/') ||
    trimmed.includes('..') ||
    trimmed.includes('\0')
  ) {
    throw new Error('Invalid column name: contains disallowed characters');
  }
  return encodeURIComponent(trimmed);
}

/** Column schema definition format for AI enrichment (backend column schema type). */
export type ColumnDefinitionType =
  | 'defined_signal'
  | 'ai_code_generation_instruction';

export interface ColumnDefinition {
  column_definition_type: ColumnDefinitionType;
  value: string;
}

export interface Column {
  col_name: string;
  col_idx?: number;
  col_type?: string;
  description?: string;
  /** AI enrichment definition. Column is AI-enabled when set. */
  definition?: ColumnDefinition;
}

export interface Cell {
  row_id: number;
  column_name: string;
  cell_value?: string;
}

/** List display separator when normalizing list values for payload (matches Spreadsheet display). */
const LIST_PAYLOAD_SEPARATOR = ', ';

/** Ensures cell_value is always a string before sending to the backend (avoids saving objects and "[object Object]"). Supports list format: arrays are sent as comma-separated string. Univer rich text (p) is extracted; other unrecognized objects are not persisted as JSON. */
function normalizeCellValueForPayload(val: unknown): string {
  if (val == null || val === '') return '';
  if (typeof val === 'string') {
    if (val === '[object Object]') return '';
    return val;
  }
  if (typeof val === 'number' || typeof val === 'boolean') return String(val);
  if (typeof val === 'object') {
    const obj = val as Record<string, unknown>;
    if (Array.isArray(obj)) {
      return obj
        .map((item) => normalizeCellValueForPayload(item))
        .join(LIST_PAYLOAD_SEPARATOR);
    }
    if ('p' in obj && obj.p != null && typeof obj.p === 'object') {
      const body = (obj.p as Record<string, unknown>)?.body as
        | { dataStream?: string }
        | undefined;
      const stream = body?.dataStream;
      if (typeof stream === 'string') {
        return stream.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
      }
    }
    if ('v' in obj || 's' in obj || 't' in obj) {
      const v = obj.v;
      return v == null || v === '' ? '' : normalizeCellValueForPayload(v);
    }
    return '';
  }
  return String(val);
}

/** Response shape for GET /v1/campaigns/{campaign_id}/schema/available-defined-signals */
export interface AvailableSignalsResponse {
  signal_names: string[];
}

export async function getAvailableDefinedSignals(
  campaignId: string,
  viewType?: CampaignSpreadsheetViewType
): Promise<AvailableSignalsResponse> {
  const userSessionToken = getUserSessionCookie();
  const url = withViewTypeQueryParam(
    `${BASE_URL}/v1/campaigns/${campaignId}/schema/available-defined-signals`,
    viewType
  );
  const response = await apiFetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${userSessionToken}`
    }
  });
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
}

export async function getColumns(
  campaignId: string,
  viewType?: CampaignSpreadsheetViewType
) {
  const userSessionToken = getUserSessionCookie();
  const url = withViewTypeQueryParam(
    `${BASE_URL}/v1/campaigns/${campaignId}/schema/columns`,
    viewType
  );
  const response = await apiFetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${userSessionToken}`
    }
  });
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
}

export async function createColumn(
  campaignId: string,
  column: Column,
  viewType?: CampaignSpreadsheetViewType
) {
  const userSessionToken = getUserSessionCookie();
  const url = withViewTypeQueryParam(
    `${BASE_URL}/v1/campaigns/${campaignId}/schema/columns`,
    viewType
  );
  const response = await apiFetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${userSessionToken}`
    },
    body: JSON.stringify(column)
  });
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
}

export async function updateColumn(
  campaignId: string,
  columnName: string,
  updates: Partial<Column>,
  viewType?: CampaignSpreadsheetViewType
) {
  const userSessionToken = getUserSessionCookie();
  const encodedColumnName = validateAndEncodeColumnName(columnName);
  const baseUrl = `${BASE_URL}/v1/campaigns/${campaignId}/schema/columns/${encodedColumnName}`;
  const url = withViewTypeQueryParam(baseUrl, viewType);
  const response = await apiFetch(url, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${userSessionToken}`
    },
    body: JSON.stringify(updates)
  });
  if (!response.ok) {
    const fallback = `Could not update column (${response.status})`;
    const msg = await getErrorMessageFromResponse(response, fallback);
    throw new Error(msg);
  }
  return response.json();
}

/** Body for PATCH .../schema/columns/{column_name}/reorder */
export interface ColumnReorderRequest {
  new_col_idx: number;
}

export async function reorderColumn(
  campaignId: string,
  columnName: string,
  body: ColumnReorderRequest,
  viewType?: CampaignSpreadsheetViewType
): Promise<unknown> {
  const userSessionToken = getUserSessionCookie();
  const encodedColumnName = validateAndEncodeColumnName(columnName);
  const baseUrl = `${BASE_URL}/v1/campaigns/${campaignId}/schema/columns/${encodedColumnName}/reorder`;
  const url = withViewTypeQueryParam(baseUrl, viewType);
  const response = await apiFetch(url, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${userSessionToken}`
    },
    body: JSON.stringify(body)
  });
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
}

export async function deleteColumn(
  campaignId: string,
  columnName: string,
  viewType?: CampaignSpreadsheetViewType
) {
  const userSessionToken = getUserSessionCookie();
  const encodedColumnName = validateAndEncodeColumnName(columnName);
  const baseUrl = `${BASE_URL}/v1/campaigns/${campaignId}/schema/columns/${encodedColumnName}`;
  const url = withViewTypeQueryParam(baseUrl, viewType);
  const response = await apiFetch(url, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${userSessionToken}`
    }
  });
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
}

export async function getCells(
  campaignId: string,
  viewType?: CampaignSpreadsheetViewType
): Promise<{ cells: Cell[] }> {
  const userSessionToken = getUserSessionCookie();
  const url = withViewTypeQueryParam(
    `${BASE_URL}/v1/campaigns/${campaignId}/cells`,
    viewType
  );
  const response = await apiFetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${userSessionToken}`
    }
  });
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
}

export async function putCells(
  campaignId: string,
  cells: Cell[],
  viewType?: CampaignSpreadsheetViewType
): Promise<{ cells?: Cell[] }> {
  const userSessionToken = getUserSessionCookie();
  const normalizedCells: Cell[] = cells.map((c) => ({
    row_id: c.row_id,
    column_name: c.column_name,
    cell_value: normalizeCellValueForPayload(c.cell_value)
  }));
  const url = withViewTypeQueryParam(
    `${BASE_URL}/v1/campaigns/${campaignId}/cells`,
    viewType
  );
  const response = await apiFetch(url, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${userSessionToken}`
    },
    body: JSON.stringify({ cells: normalizedCells })
  });
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
}

/** Cell identifier for bulk delete (row_id + column_name). */
export interface CellIdentifier {
  row_id: number;
  column_name: string;
}

export async function deleteCells(
  campaignId: string,
  cells: CellIdentifier[],
  viewType?: CampaignSpreadsheetViewType
): Promise<unknown> {
  const userSessionToken = getUserSessionCookie();
  const url = withViewTypeQueryParam(
    `${BASE_URL}/v1/campaigns/${campaignId}/cells`,
    viewType
  );
  const response = await apiFetch(url, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${userSessionToken}`
    },
    body: JSON.stringify({ cells })
  });
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
}

/** Single cell target for execute (enrichment). */
export interface ExecuteCellTarget {
  row_id: number;
  column_name: string;
}

/** Request body for POST cells/execute (enrichment). List of { row_id, column_name } pairs. */
export interface ExecuteCellsParams {
  cells: ExecuteCellTarget[];
}

/** Response from POST cells/execute (enrichment). */
export interface ExecuteCellsResponse {
  temporal_workflow_id: string;
}

export async function executeCells(
  campaignId: string,
  params: ExecuteCellsParams,
  viewType?: CampaignSpreadsheetViewType
): Promise<ExecuteCellsResponse> {
  const userSessionToken = getUserSessionCookie();
  const url = withViewTypeQueryParam(
    `${BASE_URL}/v1/campaigns/${campaignId}/cells/execute`,
    viewType
  );
  const response = await apiFetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${userSessionToken}`
    },
    body: JSON.stringify(params)
  });
  if (!response.ok) {
    const message = await getErrorMessageFromResponse(
      response,
      `HTTP error! status: ${response.status}`
    );
    throw new Error(message);
  }
  return response.json();
}

/** Get the active (RUNNING) cell executor orchestrator workflow ID. Returns null if 404 (no active workflow). */
export async function getActiveCellExecutorWorkflowId(
  campaignId: string,
  viewType?: CampaignSpreadsheetViewType
): Promise<{ workflow_id: string } | null> {
  const userSessionToken = getUserSessionCookie();
  const url = withViewTypeQueryParam(
    `${BASE_URL}/v1/campaigns/${campaignId}/cells/execute/active-workflow-id`,
    viewType
  );
  const response = await apiFetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${userSessionToken}`
    }
  });
  if (response.status === 404) {
    return null;
  }
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
}

/** Item returned by GET .../cells/execute/{workflow_id}/enriching (cell currently being enriched). */
export interface EnrichingCellItem {
  column_name: string;
  row_id: number;
}

/** Get cells currently in the process of enriching for a workflow. */
export async function getEnrichingCells(
  campaignId: string,
  workflowId: string,
  viewType?: CampaignSpreadsheetViewType
): Promise<EnrichingCellItem[]> {
  const userSessionToken = getUserSessionCookie();
  const encodedWorkflowId = encodeURIComponent(workflowId);
  const url = withViewTypeQueryParam(
    `${BASE_URL}/v1/campaigns/${campaignId}/cells/execute/${encodedWorkflowId}/enriching`,
    viewType
  );
  const response = await apiFetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${userSessionToken}`
    }
  });
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  const data = await response.json();
  return Array.isArray(data) ? data : (data?.cells_enriching ?? []);
}

/** Callbacks for enrichment SSE stream. */
export interface ExecuteCellsStreamCallbacks {
  /** Called when backend sends {"sync_required": true}; trigger debounced spreadsheet sync. */
  onSyncRequired?: () => void;
  /** Called when the SSE stream ends naturally (server closes connection). */
  onComplete?: () => void;
  onError: (message: string) => void;
}

type ExecuteCellsStreamEvent =
  | Cell
  | {
      sync_required?: boolean;
      enrichment_complete?: boolean;
      status?: string;
      error?: string;
    };

/** Subscribe to SSE stream of completed cells for an enrichment workflow. Uses fetch so auth header is sent. */
export function streamExecuteCells(
  campaignId: string,
  workflowId: string,
  callbacks: ExecuteCellsStreamCallbacks,
  viewType?: CampaignSpreadsheetViewType
): () => void {
  const userSessionToken = getUserSessionCookie();
  const encodedWorkflowId = encodeURIComponent(workflowId);
  const baseUrl = `${BASE_URL}/v1/campaigns/${campaignId}/cells/execute/${encodedWorkflowId}/stream`;
  const url = withViewTypeQueryParam(baseUrl, viewType);
  let cancelled = false;
  let reader: ReadableStreamDefaultReader<Uint8Array> | null = null;

  (async () => {
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Accept: 'text/event-stream',
          Authorization: `Bearer ${userSessionToken}`
        }
      });
      if (!response.ok) {
        const message = await getErrorMessageFromResponse(
          response,
          `Stream failed: ${response.status}`
        );
        callbacks.onError(message);
        return;
      }
      reader = response.body?.getReader() ?? null;
      if (!reader) {
        callbacks.onError('No response body');
        return;
      }
      const decoder = new TextDecoder();
      let buffer = '';
      while (!cancelled) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const data = line.slice(6).trim();
          if (!data) continue;
          try {
            const parsed = JSON.parse(data) as ExecuteCellsStreamEvent;
            if (
              parsed &&
              typeof parsed === 'object' &&
              (parsed as { status?: string }).status === 'failed'
            ) {
              // New backend behavior: job-level failure message in SSE stream.
              // Backend may still request a sync to reconcile partial results.
              if (
                (parsed as { sync_required?: boolean }).sync_required === true
              ) {
                callbacks.onSyncRequired?.();
              }
              const errorMsg =
                typeof (parsed as { error?: unknown }).error === 'string' &&
                (parsed as { error?: string }).error!.trim().length > 0
                  ? (parsed as { error: string }).error
                  : 'Cell execution job failed';
              callbacks.onError(errorMsg);
              cancelled = true;
              try {
                await reader.cancel();
              } catch {
                // ignore
              }
              break;
            }

            if (
              parsed &&
              typeof parsed === 'object' &&
              (parsed as { sync_required?: boolean }).sync_required === true
            ) {
              callbacks.onSyncRequired?.();
            }
            if (
              parsed &&
              typeof parsed === 'object' &&
              (parsed as { enrichment_complete?: boolean })
                .enrichment_complete === true
            ) {
              callbacks.onComplete?.();
            }
          } catch {
            // ignore non-JSON or unexpected lines
          }
        }
      }
      if (!cancelled) {
        callbacks.onComplete?.();
      }
    } catch (err) {
      if (!cancelled) {
        callbacks.onError(err instanceof Error ? err.message : 'Stream error');
      }
    }
  })();

  return () => {
    cancelled = true;
    if (reader) {
      reader.cancel().catch(() => {});
    }
  };
}
